import NavLinkAdapter from "@fuse/core/NavLinkAdapter";
import clsx from "clsx";
import { type ReactNode, useState, useEffect } from "react";
import { Button, Collapse, IconButton, type SxProps, Tooltip, Typography, type TypographyProps } from "@mui/material";
import FuseNavBadge from "../../FuseNavBadge";
import FuseSvgIcon from "../../../FuseSvgIcon";
import type { FuseNavItemType } from "../../types/FuseNavItemType";
import isUrlInChildren from "../../isUrlInChildren";
import usePathname from "@fuse/hooks/usePathname";
import Link from "@fuse/core/Link";
import type { ISpaceMenuTipoRestricao } from "@/api/space/dtos/authenticator.dto";
import { META_RESTRICAO } from "@/permissoes/menuPermissoes";
import useIntencaoNavegacao from "./useIntencaoNavegacao";

export interface FuseNavVerticalBaseProps {
	item: FuseNavItemType;
	nestedLevel?: number;
	onItemClick?: (item: FuseNavItemType) => void;
	aoAnteciparItem?: (item: FuseNavItemType) => void;
	className?: string;
	component?: React.ElementType;
	children?: ReactNode;
	showIcon?: boolean;
	primaryTitleProps?: TypographyProps;
	subtitleProps?: TypographyProps;
	sx?: SxProps;
	checkPermission?: boolean;
	dense?: boolean;
}

function needsToBeOpened(pathname: string, item: FuseNavItemType) {
	return pathname && isUrlInChildren(item, pathname);
}

function handleExternalLinkClick(ev: React.MouseEvent<HTMLElement>, url?: string, disabled = false) {
	ev.preventDefault();
	ev.stopPropagation();

	if (disabled || !url) return;

	window.open(url, "_blank", "noopener,noreferrer");
}

function obterComponenteNavegacao(
	component: React.ElementType | undefined,
	url: string | undefined,
): React.ElementType {
	if (component) return component;
	if (!url) return "div";
	return url.startsWith("http") || url.startsWith("mailto") ? Link : NavLinkAdapter;
}

function obterPropsLinkNavegacao(component: React.ElementType, item: FuseNavItemType, disabled: boolean) {
	if (component !== NavLinkAdapter && component !== Link) return {};
	const propsComuns = {
		disabled,
		to: item.url,
		role: "button" as const,
		exact: item.exact,
		"aria-disabled": disabled,
	};
	if (component === Link) return { ...propsComuns, target: item.target || "_blank" };
	return { ...propsComuns, to: item.url || "", end: item.end };
}

function AtalhoExternoNavegacao({ item, dense = false }: Readonly<Pick<FuseNavVerticalBaseProps, "item" | "dense">>) {
	const disabled = Boolean(item.disabled || !item.url);
	return (
		<Tooltip title={disabled ? "" : "Clique para abrir em uma nova aba do navegador"} placement="right" arrow>
			<span className="inline-flex shrink-0">
				<FuseSvgIcon
					size={dense ? 14 : 16}
					className={clsx(
						"arrow-icon",
						dense ? "mt-1.5 ml-1" : "mt-2 ml-2",
						disabled
							? "text-disabled cursor-not-allowed opacity-50"
							: "text-primary-foreground/70 hover:text-primary-foreground/90 hover:cursor-pointer",
					)}
					color={disabled ? "disabled" : "inherit"}
					onClick={(event) => handleExternalLinkClick(event, item.url, disabled)}
				>
					lucide:external-link
				</FuseSvgIcon>
			</span>
		</Tooltip>
	);
}

function RestricaoBadge({
	tipo,
}: Readonly<{
	tipo?: ISpaceMenuTipoRestricao | null;
}>) {
	if (!tipo) return null;

	const meta = META_RESTRICAO[tipo];

	return (
		<span
			className={clsx(
				"inline-flex max-w-full shrink-0 items-center self-start gap-1 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide",
				meta.badgeColor,
			)}
		>
			<FuseSvgIcon size={10} className="shrink-0">
				{meta.icon}
			</FuseSvgIcon>
			<span className="whitespace-nowrap">{meta.badge}</span>
		</span>
	);
}

function ItemComTooltipRestricao({ item, children }: Readonly<Pick<FuseNavVerticalBaseProps, "item" | "children">>) {
	const tipo = item.restricaoTipo;
	if (!tipo) return children;
	const meta = META_RESTRICAO[tipo];
	return (
		<Tooltip title={`${meta.badge}: ${meta.tooltip}`} placement="right" arrow describeChild>
			<span className="block min-w-0 w-full rounded-md">{children}</span>
		</Tooltip>
	);
}

function ConteudoItemNavegacao({
	item,
	showIcon = true,
	primaryTitleProps = {},
	subtitleProps = {},
	dense = false,
	nestedLevel = 0,
}: Readonly<
	Pick<
		FuseNavVerticalBaseProps,
		"item" | "showIcon" | "primaryTitleProps" | "subtitleProps" | "dense" | "nestedLevel"
	>
>) {
	const isGroup = item.type === "group";
	return (
		<>
			{!isGroup && showIcon && item.icon && (
				<FuseSvgIcon className={clsx("fuse-list-item-icon", "shrink-0", item.iconClass)}>
					{item.icon}
				</FuseSvgIcon>
			)}
			<div className="flex min-w-0 flex-auto flex-col items-start gap-1">
				{item.title && (
					<Typography
						{...primaryTitleProps}
						{...(isGroup && { color: "secondary" })}
						className={clsx(
							"fuse-list-item-title wrap-break-word max-w-full",
							dense
								? "text-(length:--sidebar-font-size,12px) leading-(--sidebar-line-height,16px)"
								: "leading-none",
							(isGroup || nestedLevel === 0) && "font-medium",
							primaryTitleProps.className,
						)}
					>
						{item.title}
					</Typography>
				)}
				{item.subtitle && (
					<Typography
						color="text.secondary"
						{...subtitleProps}
						className={clsx(
							"fuse-list-item-subtitle text-sm leading-none wrap-break-word max-w-full",
							subtitleProps.className,
						)}
					>
						{item.subtitle}
					</Typography>
				)}
				<RestricaoBadge tipo={item.restricaoTipo} />
			</div>
			{item.badge && <FuseNavBadge badge={item.badge} />}
		</>
	);
}

/**
 * O FuseNavVerticalItemBase é um componente base compartilhado para todos os itens de navegação vertical.
 */
function FuseNavVerticalItemBase(props: Readonly<FuseNavVerticalBaseProps>) {
	const {
		item,
		nestedLevel = 0,
		onItemClick,
		aoAnteciparItem,
		className,
		component,
		showIcon = true,
		primaryTitleProps = {},
		subtitleProps = {},
		sx,
		checkPermission,
		dense = false,
	} = props;

	const pathname = usePathname();
	const eventosIntencao = useIntencaoNavegacao(item, aoAnteciparItem);

	const isGroup = item.type === "group";
	const isCollapsable = item.type === "collapse";
	const isDisabled = Boolean(item.disabled);

	const buttonComponent = obterComponenteNavegacao(component, item.url);
	const [open, setOpen] = useState(() => needsToBeOpened(pathname, item));

	useEffect(() => {
		if (isCollapsable && item.url) setOpen((prev) => prev || needsToBeOpened(pathname, item));
	}, [pathname, item, isCollapsable]);

	const itemProps = obterPropsLinkNavegacao(buttonComponent, item, isDisabled);

	function handleClick(e: React.MouseEvent<HTMLElement>) {
		if (isDisabled) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}

		e.stopPropagation();

		if (isCollapsable) setOpen((prev) => prev || !prev);

		if (onItemClick) onItemClick(item);
	}

	if (item.hidden) return null;

	if (checkPermission && !item?.hasPermission) return null;

	return (
		<>
			<ItemComTooltipRestricao item={item}>
				<Button
					component={buttonComponent}
					className={clsx(
						"flex w-full items-center justify-start rounded-md",
						dense
							? "mb-0.5 min-h-(--sidebar-item-height,26px) gap-1.5 px-1.5 py-1 text-left"
							: "mb-1 gap-2 p-2",
						item.active && "active",
						(item.subtitle || item.restricaoTipo) && "items-start",
						className,
						isGroup && (dense ? "my-2" : "my-4"),
					)}
					sx={{
						...sx,
						"&.Mui-disabled": { color: "text.secondary" },
						"&.active .fuse-list-item-title": { fontWeight: 600 },
						"&.active": {
							backgroundColor: (theme) =>
								theme.palette.mode === "light"
									? "rgba(0, 0, 0, .05)!important"
									: "rgba(255, 255, 255, .1)!important",
						},
					}}
					color="inherit"
					disabled={isDisabled}
					onClick={handleClick}
					aria-label={item.title}
					aria-expanded={open}
					{...itemProps}
					{...(buttonComponent === NavLinkAdapter ? eventosIntencao : {})}
				>
					<ConteudoItemNavegacao
						item={item}
						showIcon={showIcon}
						primaryTitleProps={primaryTitleProps}
						subtitleProps={subtitleProps}
						dense={dense}
						nestedLevel={nestedLevel}
					/>

					{isCollapsable && (
						<IconButton
							disableRipple
							className="h-5 w-5 p-0 hover:bg-transparent focus:bg-transparent"
							disabled={isDisabled}
							onClick={(ev) => {
								ev.preventDefault();
								ev.stopPropagation();

								if (isDisabled) return;

								setOpen(!open);
							}}
						>
							<FuseSvgIcon size={dense ? 14 : 16} className="arrow-icon" color="inherit">
								{open ? "lucide:chevron-down" : "lucide:chevron-right"}
							</FuseSvgIcon>
						</IconButton>
					)}
				</Button>
			</ItemComTooltipRestricao>

			{isCollapsable && (
				<Collapse
					in={open}
					className="collapse-children"
					sx={{
						...(nestedLevel < 4 && {
							paddingLeft: dense ? "12px" : "16px",
							"& > .MuiCollapse-wrapper": {
								borderLeft: "1px solid",
								borderColor: "divider",
								"& > .MuiCollapse-wrapperInner": {
									paddingLeft: dense ? "6px" : "8px",
								},
							},
						}),
					}}
				>
					{item?.children?.map((_item) => {
						if (_item.hidden) return null;

						const subitem = (
							<FuseNavVerticalItemBase
								key={_item.id}
								item={_item}
								nestedLevel={nestedLevel + 1}
								onItemClick={onItemClick}
								aoAnteciparItem={aoAnteciparItem}
								checkPermission={checkPermission}
								dense={dense}
							/>
						);
						if (_item.children?.length) return subitem;
						return (
							<div className="flex items-start" key={_item.id}>
								{subitem}
								<AtalhoExternoNavegacao item={_item} dense={dense} />
							</div>
						);
					})}
				</Collapse>
			)}

			{isGroup &&
				item?.children?.map((_item) => (
					<FuseNavVerticalItemBase
						key={_item.id}
						item={_item}
						nestedLevel={nestedLevel}
						onItemClick={onItemClick}
						aoAnteciparItem={aoAnteciparItem}
						checkPermission={checkPermission}
						dense={dense}
					/>
				))}
		</>
	);
}

export default FuseNavVerticalItemBase;
